import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GeneratedImage, GenerationResult } from '@gilding/shared';
import { IMAGE_MODEL_PROFILES, STYLE_PROFILES } from '@gilding/shared';
import { api } from '../api/client.js';
import { FixDialog } from '../components/FixDialog.js';
import { TraitReport } from '../components/TraitReport.js';
import { Reveal } from '../components/Reveal.js';
import { GildedImage } from '../components/GildedImage.js';
import { GildUnveil } from '../components/GildUnveil.js';
import { GildedBrief } from '../components/GildedBrief.js';
import { saveImage } from '../lib/saveImage.js';
import { SaveSheet, isCoarsePointer } from '../components/SaveSheet.js';
import { GildButton } from '../components/GildButton.js';
import { cn } from '../lib/classnames.js';

export function Result() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navState = (location.state ?? null) as GenerationResult | null;

  // Prefer state passed via navigate (fresh after generate); fall back to a
  // server fetch when the user opens the URL directly (e.g. from gallery).
  const fetchedQuery = useQuery({
    queryKey: ['gen', routeId],
    queryFn: () => api.generation(routeId!),
    enabled: !navState && !!routeId,
    staleTime: 30_000,
  });

  const state =
    navState ??
    (fetchedQuery.data && fetchedQuery.data.kind === 'full' ? fetchedQuery.data.result : null);

  const [copied, setCopied] = useState(false);
  const [fixTarget, setFixTarget] = useState<GeneratedImage | null>(null);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);
  const [saveSheet, setSaveSheet] = useState<{ url: string; filename: string } | null>(null);
  // Which variant becomes the feed cover when publishing. Defaults to the first,
  // then reflects the piece's stored cover once it loads so re-publishing
  // preserves the owner's earlier choice.
  const [coverIndex, setCoverIndex] = useState(0);
  useEffect(() => {
    if (state) setCoverIndex(state.coverIndex ?? 0);
  }, [state?.id]);

  const onSaveImage = async (url: string, idx: number): Promise<void> => {
    const filename = `gilding-${state?.id ?? 'image'}-${idx + 1}.png`;
    // Touch devices: the long-press viewer is the only dependable route to
    // the photo library (iOS share sheets hide "Save Image" for web files).
    if (isCoarsePointer()) {
      setSaveSheet({ url, filename });
      return;
    }
    setSavingIdx(idx);
    try {
      await saveImage(url, filename);
    } finally {
      setSavingIdx(null);
    }
  };

  // The unveil ceremony — a gold wash lifts off each plate — plays exactly
  // once per generation, on the visit that delivered it. Revisits (gallery,
  // back-button, refresh) get the quiet page.
  const [ceremony] = useState<boolean>(() => {
    if (!navState || typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(`gilding_unveiled_${navState.id}`) === null;
    } catch {
      return true;
    }
  });
  useEffect(() => {
    if (!navState) return;
    try {
      window.sessionStorage.setItem(`gilding_unveiled_${navState.id}`, '1');
    } catch {
      /* private mode — ceremony may replay, which is harmless */
    }
  }, [navState]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const publishMut = useMutation({
    mutationFn: (cover: number) => api.publish(state!.id, cover),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gen', state?.id] });
      void queryClient.invalidateQueries({ queryKey: ['mine'] });
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  // Cover is its own persisted property — picking one saves it immediately via
  // the dedicated endpoint (which also re-syncs the feed card), so it sticks
  // everywhere with no separate "save" step and can't drift out of sync.
  const setCoverMut = useMutation({
    mutationFn: (i: number) => api.setCover(state!.id, i),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gen', state?.id] });
      void queryClient.invalidateQueries({ queryKey: ['mine'] });
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });
  const chooseCover = (i: number): void => {
    setCoverIndex(i);
    setCoverMut.mutate(i);
  };
  const unpublishMut = useMutation({
    mutationFn: () => api.unpublish(state!.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gen', state?.id] });
      void queryClient.invalidateQueries({ queryKey: ['mine'] });
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  if (fetchedQuery.isLoading && !state) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <p className="font-display italic text-ink-subtle">Loading the gilding</p>
      </div>
    );
  }
  if (!state) {
    return (
      <section className="flex flex-col items-center gap-5 py-16 text-center">
        <QuillEmptyMark />
        <h2 className="heading-section">No gilding loaded</h2>
        <p className="max-w-sm font-display italic text-ink-soft">
          This page expects a freshly generated piece, or a saved one opened from the gallery.
        </p>
        <Link to="/new" className="btn-primary">
          Start a new gilding
        </Link>
      </section>
    );
  }

  const isPublic = state.visibility === 'public';
  // Remixes can't be published — the feed shows original scenes, not everyone's
  // re-render of the same one. (A direct remix sets remixSourceId; the server
  // also blocks fixes descended from a remix, surfaced via the publish error.)
  const isRemix = !!state.request.remixSourceId;
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/remix/${state.id}`
    : `/remix/${state.id}`;

  const styleLabel = STYLE_PROFILES[state.request.stylePreset]?.label ?? state.request.stylePreset;

  const copyShare = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const handleFixSuccess = (next: GenerationResult): void => {
    setFixTarget(null);
    navigate(`/result/${next.id}`, { state: next });
  };

  return (
    <div className="flex flex-col gap-12">
      <Reveal as="header" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="heading-display">
            <span className="font-display italic">{styleLabel}</span>
            <span className="text-ink-subtle"> · </span>
            <span>
              {state.images.length} {state.images.length === 1 ? 'variant' : 'variants'}
            </span>
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            {isRemix ? null : isPublic ? (
              <span className="tag inline-flex items-center gap-1.5">
                <CheckIcon />
                On the feed
              </span>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={() => publishMut.mutate(coverIndex)}
                disabled={publishMut.isPending}
              >
                {publishMut.isPending ? 'Publishing' : 'Publish to feed'}
              </button>
            )}
            <GildButton onClick={() => navigate("/new")}>Gild another</GildButton>
          </div>
        </div>
        <p className="tag">
          {state.creditsDebited} {state.creditsDebited === 1 ? 'leaf' : 'leaves'} spent
          <span className="hairline-v mx-2 inline-block h-3" />
          <VisibilityLabel isPublic={isPublic} />
        </p>
        {state.notice && (
          <p className="rounded-sm border border-hairline-strong/60 bg-surface-elevated px-4 py-2.5 font-display italic text-caption text-ink-soft">
            {state.notice}
          </p>
        )}
      </Reveal>

      {/* Pick-your-favourite nudge — only when there's a choice to make and it
          isn't already public. The cover variant wears the gilded ring below. */}
      {!isRemix && !isPublic && state.images.length > 1 && (
        <Reveal as="p" className="-mt-6 font-display italic text-caption text-ink-subtle">
          Pick your favourite. The one ringed in gold becomes the feed cover. Tap
          “Use as cover” on another to switch.
        </Reveal>
      )}

      {/* Image variants — the hero. Plate frame, no pill chips, with a discreet
          Fix action. On a fresh generation each plate is unveiled: the gold
          wash lifts off left-to-right, second plate a beat after the first. */}
      <section className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {state.images.map((img, i) => (
          <Reveal
            as="figure"
            key={`${img.model}-${img.idx}-${i}`}
            delay={i * 110}
            className="flex flex-col gap-3"
          >
            <div
              className={cn(
                'card-plate relative overflow-hidden transition-shadow duration-300',
                state.images.length > 1 &&
                  coverIndex === i &&
                  'ring-2 ring-inset ring-accent',
              )}
              style={
                state.request.aspectRatio
                  ? { aspectRatio: state.request.aspectRatio.replace(':', ' / ') }
                  : undefined
              }
            >
              <GildedImage
                src={img.url}
                alt={`Gilding variant ${i + 1}`}
                loading="lazy"
                decoding="async"
                className={
                  state.request.aspectRatio
                    ? 'block h-full w-full object-cover'
                    : 'block h-auto w-full'
                }
              />
              {ceremony && <GildUnveil src={img.url} delayMs={300 + i * 220} />}
            </div>
            <figcaption className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-1">
              <span className="tag inline-flex items-center gap-1.5">
                {IMAGE_MODEL_PROFILES[img.model].label}
                <span className="text-ink-subtle"> · variant {romanNumeral(img.idx + 1)}</span>
                {state.images.length > 1 && coverIndex === i && (
                  <span className="inline-flex items-center gap-1 text-accent">
                    <CheckIcon />
                    feed cover
                  </span>
                )}
              </span>
              <span className="flex items-baseline gap-3">
                {state.images.length > 1 && coverIndex !== i && (
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={setCoverMut.isPending}
                    onClick={() => chooseCover(i)}
                  >
                    {setCoverMut.isPending ? 'Saving…' : 'Use as cover'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={savingIdx !== null}
                  onClick={() => void onSaveImage(img.url, i)}
                >
                  {savingIdx === i ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setFixTarget(img)}
                >
                  Fix this
                </button>
              </span>
            </figcaption>
            <TraitReport id={state.id} index={i} />
          </Reveal>
        ))}
      </section>

      {/* The amplified prompt — the illuminated brief Claude wrote. */}
      <Reveal as="section">
        <GildedBrief amplified={state.amplified.amplified} negative={state.amplified.negative} />
      </Reveal>

      {/* Publish / share — museum donation card. The single .card on the page. */}
      <Reveal as="section" className="card flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h2 className="heading-section">
            {isRemix
              ? 'A remix, yours to keep'
              : isPublic
                ? 'On the public feed'
                : 'Send it out into the world'}
          </h2>
          <p className="font-display italic text-ink-soft">
            {isRemix
              ? 'Remixes aren’t posted to the public feed. The feed shows original scenes, not everyone’s re-render of the same one. This stays in your gallery; save the image or share it directly.'
              : isPublic
                ? 'Anyone with the link can remix this gilding with their own character. They see the image, then a prompt re-derived for them. Your typed sketch stays yours.'
                : 'Publishing posts the image, not the prompt, to the public feed and creates a remix invitation. Your typed sketch always stays private.'}
          </p>
          {!isRemix && state.images.length > 1 && (
            <p className="mt-1 font-display italic text-caption text-ink-subtle">
              {isPublic
                ? 'The variant marked “feed cover” above is what the feed shows. Tap “Use as cover” on another, then “Update feed cover” to switch it. No need to unpublish.'
                : 'The variant marked “feed cover” above is the one shown in the feed. Tap “Use as cover” on the other to switch.'}
            </p>
          )}
        </div>

        {!isRemix && (
        <div className="flex flex-wrap items-center gap-3">
          {isPublic ? (
            <>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void copyShare()}
                disabled={publishMut.isPending || unpublishMut.isPending}
              >
                {copied ? (
                  <>
                    <CheckIcon />
                    Link copied
                  </>
                ) : (
                  <>
                    <CopyIcon />
                    Copy invitation link
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => unpublishMut.mutate()}
                disabled={unpublishMut.isPending}
              >
                {unpublishMut.isPending ? 'Withdrawing' : 'Withdraw from feed'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn-primary"
              onClick={() => publishMut.mutate(coverIndex)}
              disabled={publishMut.isPending}
            >
              {publishMut.isPending ? 'Publishing' : 'Publish to the feed'}
            </button>
          )}
        </div>
        )}

        {(publishMut.error || unpublishMut.error) && (
          <p className="font-display italic text-caption text-danger">
            {publishErrorMessage(publishMut.error) ??
              (unpublishMut.error as Error)?.message}
          </p>
        )}

        {isPublic && (
          <div className="card-plate flex items-center justify-between gap-4 px-4 py-3">
            <div className="flex min-w-0 flex-col">
              <p className="font-display italic text-caption text-ink-subtle">invitation link</p>
              <p className="truncate font-display italic text-lede text-ink">{shareUrl}</p>
            </div>
            <button
              type="button"
              onClick={() => void copyShare()}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm border border-hairline text-ink-soft transition-colors hover:border-accent hover:text-ink"
              aria-label={copied ? 'Copied' : 'Copy link'}
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </button>
          </div>
        )}
      </Reveal>

      {/* Original input — ancillary, plain section, eyebrow-less, italic pull-quote. */}
      {state.request.sketch && state.request.sketch !== '(remix)' && (
        <Reveal as="section" className="flex flex-col gap-4">
          <h2 className="heading-section">What you wrote</h2>
          {/* Both quotation marks are decorative and OUT of the text flow
              (absolute) — a giant inline glyph inflated the line box and forced
              ugly wraps. The text flows plainly between them. */}
          <blockquote className="relative font-display text-lede italic leading-relaxed text-ink">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -left-1 -top-3 font-display text-[3.5rem] not-italic leading-none text-accent-deep/70"
            >
              &ldquo;
            </span>
            <span className="block px-8 pb-6">{state.request.sketch.trimEnd()}</span>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute bottom-0 right-1 font-display text-[3.5rem] not-italic leading-none text-accent-deep/70"
            >
              &rdquo;
            </span>
          </blockquote>
          <p className="tag">
            {styleLabel}
            <span className="hairline-v mx-2 inline-block h-3" />
            {state.request.aspectRatio}
            <span className="hairline-v mx-2 inline-block h-3" />
            {state.request.mode}
          </p>
          <p className="font-display italic text-caption text-ink-subtle">
            Only you can see this. Even when you publish, the feed shows the image alone.
          </p>
        </Reveal>
      )}

      {state.request.remixSourceId && (
        <section className="flex flex-col gap-3">
          <h2 className="heading-section">Remixed from another gilding</h2>
          <p className="font-display italic text-ink-soft">
            This was generated from a public gilding. The amplified prompt above was re-derived for{' '}
            <em>your</em> character. The original creator&apos;s sketch is not shown.
          </p>
        </section>
      )}

      {saveSheet && (
        <SaveSheet
          url={saveSheet.url}
          filename={saveSheet.filename}
          onClose={() => setSaveSheet(null)}
        />
      )}

      {fixTarget && (
        <FixDialog
          parent={state}
          image={fixTarget}
          open={true}
          onClose={() => setFixTarget(null)}
          onSuccess={handleFixSuccess}
        />
      )}
    </div>
  );
}

/* ---------- small inline UI bits ---------- */

/** Friendly publish error — translates the server's remix block, falls back to
 *  the raw message for anything else. */
function publishErrorMessage(err: unknown): string | null {
  if (!err) return null;
  const e = err as { code?: string; message?: string };
  if (e.code === 'remix_not_publishable') {
    return 'Remixes can’t be published to the feed. They stay in your gallery.';
  }
  return e.message ?? 'Could not publish. Try again.';
}

function VisibilityLabel({ isPublic }: { isPublic: boolean }) {
  return isPublic ? (
    <span className="inline-flex items-center gap-1.5">
      <EyeIcon />
      On the feed
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <LockIcon />
      Just for you
    </span>
  );
}

function EyeIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1.5 8C3 4.5 5.4 3 8 3s5 1.5 6.5 5c-1.5 3.5-3.9 5-6.5 5S3 11.5 1.5 8Z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.5" y="7.5" width="9" height="6" rx="1" />
      <path d="M5.5 7.5V5.25a2.5 2.5 0 0 1 5 0V7.5" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5" y="5" width="8" height="9" rx="1" />
      <path d="M3 11V3a1 1 0 0 1 1-1h7" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

function QuillEmptyMark() {
  // No-source illustration — quill resting on parchment, hand-drawn feel.
  return (
    <svg
      width="120"
      height="120"
      viewBox="0 0 120 120"
      fill="none"
      stroke="currentColor"
      className="text-ink-subtle"
      aria-hidden="true"
    >
      <path
        d="M18 92h84"
        strokeWidth="1"
        strokeLinecap="round"
      />
      <path
        d="M28 86c8-30 30-50 64-58-6 32-26 52-58 60"
        strokeWidth="1.25"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M40 78c12-22 26-36 44-44"
        strokeWidth="0.7"
        strokeLinecap="round"
      />
      <circle cx="32" cy="86" r="2" fill="currentColor" stroke="none" opacity="0.5" />
    </svg>
  );
}

function romanNumeral(n: number): string {
  const map: Record<number, string> = {
    1: 'I',
    2: 'II',
    3: 'III',
    4: 'IV',
    5: 'V',
    6: 'VI',
    7: 'VII',
    8: 'VIII',
    9: 'IX',
    10: 'X',
  };
  return map[n] ?? String(n);
}

